import useToastContext from '@contexts/ToastContext/hook';
import { FindBestByProductsResponse } from '@interfaces/ranking';
import { useEffect, useMemo, useRef, useState } from 'react';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import { VibTooltip } from '@vibTheme/components/VibTooltip';
import { Typography } from '@vibTheme/components/_base/Typography';
import { Link } from '@vibTheme/components/_base/Link';
import { findBestByProducts } from '@api/ranking';
import useGroupsContext from '@contexts/GroupsContext/hook';
import useFeaturePermissionsContext from '@contexts/FeaturePermissionsContext/hook';
import { permiteProduto } from '@utils/permissionFilter';
import { filtrarComunidadesPorPapel } from '@utils/comunidadesSelecionaveis';
import { UserRoles } from '@interfaces/auth';

/**
 * Quem responde pela comunidade, e por isso lê a posição dela no ranking de vendas.
 *
 * Afiliado e indicador ficam de fora de propósito: os dois alcançam a comunidade por um vínculo de
 * comissão, e a posição do produto no ranking não é sobre a comissão deles.
 */
const PAPEIS_QUE_RESPONDEM = [
  UserRoles.OWNER,
  UserRoles.COPRODUCER,
  UserRoles.COLLABORATOR,
];

export const RankingDisplay = () => {
  const [rankings, setRankings] = useState<FindBestByProductsResponse>([]);
  const [position, setPosition] = useState(0);
  const [shallRevalidate, setShallRevalidate] = useState(false);
  const { communities } = useGroupsContext();
  const { getUserPermissions } = useFeaturePermissionsContext();

  /*
    Papel E permissão, e não só permissão.

    O recorte foi `[OWNER, COPRODUCER]`, e o problema real dali era o colaborador: com o relatório
    de vendas concedido pelo dono, ele não via troféu nenhum. A correção trocou o critério inteiro
    por `reportInvoices:view` — e essa feature é `111` na matriz, ou seja, vale também para o
    AFILIADO.

    O que isso produziu está no relato que trouxe esta correção: uma criadora com três afiliações
    parou de ver a própria comunidade. `findBestByProducts` devolve as TRÊS MELHORES colocadas entre
    os ids que recebe, e as afiliações dela ranqueavam 160, 286 e 489 contra 707 da comunidade
    dela — que virou a quinta do próprio conjunto e sumiu da resposta. Com o rodízio de 60s
    consertado na mesma leva, o que ela vê é três nomes que não são dela trocando de minuto em
    minuto.

    A pergunta que o troféu responde é "como vai a MINHA comunidade". A do afiliado não é dele: a
    relação dele é com a comissão que ganha, e não com o desempenho do produto. Então o conjunto é
    quem RESPONDE pela comunidade — dono, coprodutor e colaborador —, e dentro dele ainda vale a
    permissão, que é o que o grant do dono pode tirar de um colaborador.

    Indicador sai pelo mesmo motivo do afiliado. Quem é afiliada E colaboradora da mesma comunidade
    entra: a colaboração é decisão explícita do dono, e `communities` traz os dois papéis.
  */
  const idsDoTrofeu = filtrarComunidadesPorPapel(
    communities,
    PAPEIS_QUE_RESPONDEM,
  )
    .filter((community) =>
      permiteProduto(
        getUserPermissions,
        { feature: 'reportInvoices', action: 'view' },
        community.id,
      ),
    )
    .map((community) => community.id);

  const botConfigIds = useMemo(
    () => idsDoTrofeu,
    [JSON.stringify(idsDoTrofeu)],
  );
  const toast = useToastContext();
  /*
    `position` não é reiniciado quando a lista encolhe — e agora ela encolhe, porque o conjunto de
    comunidades muda no meio da sessão. Sem o módulo aqui, o índice fica fora do intervalo e o JSX
    abaixo lê `productName` de `undefined`.
  */
  const currentProduct = rankings.length
    ? rankings[position % rankings.length]
    : undefined;

  const fetchRankings = async (atual: () => boolean) => {
    /*
      Lista vazia não vai para a rota: ela não recorta nada do lado do servidor — confia nos ids que
      recebe —, então mandar vazio é pedir um resultado que ninguém filtrou. Sem comunidade
      permitida, o troféu não aparece.
    */
    if (!botConfigIds.length) {
      setRankings([]);
      return;
    }

    try {
      const data = await findBestByProducts({ botConfigIds });

      /*
        A resposta do conjunto ANTIGO não escreve: o troféu vive na barra superior, montado o tempo
        todo, e o conjunto muda no meio da sessão (convite aceito, grant revogado). Chegando por
        último, ela mostraria a posição de uma comunidade que a pessoa já não lê.
      */
      if (!atual()) return;

      setRankings(data);
    } catch (err) {
      if (!atual()) return;

      toast.handleError(
        err,
        'Ranking não disponível! Tente novamente mais tarde.',
      );
    }
  };

  /*
    O conjunto ENCOLHEU: o que saiu some agora, antes de qualquer espera.

    A resposta traz `{ position, productName }` e nenhum id, então não dá para reconciliar item a
    item — some tudo, e a busca repõe o que ainda vale. Enquanto ela está em voo, e para sempre se
    ela falhar, o troféu mostraria a posição de uma comunidade que a pessoa já não lê.

    Efeito PRÓPRIO, com `botConfigIds` sozinho nas dependências: a revalidação de 15 em 15 minutos
    roda com o mesmo conjunto, e limpar ali faria o troféu piscar sem motivo.
  */
  /* A lista corrente, para o rodízio que roda dentro de um intervalo criado uma vez só. */
  const rankingsRef = useRef(rankings);
  rankingsRef.current = rankings;

  const conjuntoAnterior = useRef(botConfigIds);

  useEffect(() => {
    const antes = conjuntoAnterior.current;
    conjuntoAnterior.current = botConfigIds;

    /*
      Só quando ALGUÉM SAIU. Ganhar acesso a mais uma comunidade não invalida o que está na tela —
      limpar ali faz o troféu sumir por uma mudança que só acrescenta, e ficar sumido até a próxima
      revalidação se a busca nova falhar.
    */
    if (antes.some((id) => !botConfigIds.includes(id))) setRankings([]);
  }, [botConfigIds]);

  /*
    `botConfigIds` entra nas dependências: ele vem do contexto de permissão, e não de uma leitura
    feita dentro da busca.
  */
  useEffect(() => {
    let valendo = true;

    fetchRankings(() => valendo);

    return () => {
      valendo = false;
    };
  }, [shallRevalidate, botConfigIds]);

  useEffect(() => {
    const revalidateCycle = setInterval(
      () => setShallRevalidate((value) => !value),
      900000,
    );
    /*
      A troca lê a lista por REFERÊNCIA, e não pelo closure.

      Este efeito tem `[]` de dependências, então `rankings` aqui dentro era para sempre o `[]` da
      primeira renderização: a guarda `rankings.length` nunca passava e o rodízio NUNCA acontecia —
      quem tem mais de uma comunidade via só a primeira, desde sempre. Pôr `rankings` nas
      dependências consertaria recriando os dois intervalos a cada resposta, e o de revalidação
      recomeçaria o relógio de 15 minutos junto.
    */
    const swapRankingDisplay = setInterval(() => {
      const total = rankingsRef.current.length;

      if (total) setPosition((anterior) => (anterior + 1) % total);
    }, 60000);
    return () => {
      clearInterval(revalidateCycle);
      clearInterval(swapRankingDisplay);
    };
  }, []);

  const resolveTrophyStyles = () => {
    switch (currentProduct?.position) {
      case 1:
        return 'text-gold';

      case 2:
        return 'text-silver';

      case 3:
        return 'text-bronze';

      default:
        return 'hidden';
    }
  };
  const trophyStyles = resolveTrophyStyles();

  /*
    A guarda é sobre o ITEM, e não sobre a lista ter tamanho: é `currentProduct` que o JSX abaixo lê.
    Com a lista encolhendo no meio da sessão, "tem alguma coisa" deixou de implicar "tem esta".
  */
  if (!currentProduct) return null;

  return (
    <VibTooltip
      title={
        <Typography className="text-white text-sm p-sm">
          Sua comunidade <strong>{currentProduct.productName}</strong> está em{' '}
          {currentProduct.position}º lugar no nosso ranking de vendas:
          <br />
          <Link
            href="https://marketplace.vibx.com.br/"
            className="text-white font-bold"
          >
            https://marketplace.vibx.com.br/
          </Link>
        </Typography>
      }
    >
      <div className="rounded-full bg-opacity-20 bg-gray-300 flex items-center w-[15rem] md:w-auto p-sm mx-auto md:mr-md">
        <div className="flex max-w-full font-semibold gap-sm">
          <TrophyIcon className={trophyStyles} />
          <p className="truncate">
            {currentProduct.position}º - {currentProduct.productName}
          </p>
        </div>
      </div>
    </VibTooltip>
  );
};
